import { useState, useCallback } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { toast } from 'sonner';
import type { UseChatOperationsReturn } from '../types';
import {
  sendMessage as sendMessageService,
  markMessagesAsRead as markMessagesAsReadService,
  deleteConversation as deleteConversationService,
  clearConversation as clearConversationService
} from '../services/chatService';
import { CHAT_CONFIG } from '../constants';

/**
 * Hook for chat operations (send message, mark as read)
 * Provides simple operations without complex global state
 */
export const useChatOperations = (): UseChatOperationsReturn => {
  const { user } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Send a message to another user
   */
  const sendMessage = useCallback(async (receiverId: string, content: string): Promise<boolean> => {
    if (!user?.id) {
      const authError = new Error('User must be authenticated to send messages');
      setError(authError);
      toast.error('You must be logged in to send messages');
      return false;
    }

    if (!receiverId) {
      const receiverError = new Error('Receiver ID is required');
      setError(receiverError);
      toast.error('Invalid recipient');
      return false;
    }

    if (!content || content.trim().length === 0) {
      const contentError = new Error('Message content cannot be empty');
      setError(contentError);
      toast.error('Message cannot be empty');
      return false;
    }

    if (content.trim().length > CHAT_CONFIG.MAX_MESSAGE_LENGTH) {
      const lengthError = new Error(`Message too long (max ${CHAT_CONFIG.MAX_MESSAGE_LENGTH} characters)`);
      setError(lengthError);
      toast.error(`Message too long (max ${CHAT_CONFIG.MAX_MESSAGE_LENGTH} characters)`);
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await sendMessageService(user.id, receiverId, content.trim());
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to send message');
      console.error('Error sending message:', errorMessage);
      setError(errorMessage);
      toast.error('Failed to send message. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Mark all messages from a sender as read
   */
  const markMessagesAsRead = useCallback(async (senderId: string): Promise<boolean> => {
    if (!user?.id) {
      const authError = new Error('User must be authenticated to mark messages as read');
      setError(authError);
      return false;
    }

    if (!senderId) {
      const senderError = new Error('Sender ID is required');
      setError(senderError);
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await markMessagesAsReadService(user.id, senderId);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to mark messages as read');
      console.error('Error marking messages as read:', errorMessage);
      setError(errorMessage);
      // Don't show toast for mark as read errors as they're not critical to user experience
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Clear conversation - delete all messages (clear chat history)
   */
  const clearConversation = useCallback(async (otherUserId: string): Promise<boolean> => {
    if (!user?.id) {
      const authError = new Error('User must be authenticated to clear conversations');
      setError(authError);
      return false;
    }

    if (!otherUserId) {
      const userError = new Error('Other user ID is required');
      setError(userError);
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await clearConversationService(user.id, otherUserId);
      toast.success('Conversation messages cleared successfully');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to clear conversation messages');
      console.error('Error clearing conversation:', errorMessage);
      setError(errorMessage);
      toast.error('Failed to clear conversation messages. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Delete conversation - remove from admin's conversation list
   */
  const deleteConversation = useCallback(async (otherUserId: string): Promise<boolean> => {
    if (!user?.id) {
      const authError = new Error('User must be authenticated to delete conversations');
      setError(authError);
      return false;
    }

    if (!otherUserId) {
      const userError = new Error('Other user ID is required');
      setError(userError);
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteConversationService(user.id, otherUserId);
      toast.success('Conversation removed from list successfully');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to remove conversation from list');
      console.error('Error deleting conversation:', errorMessage);
      setError(errorMessage);
      toast.error('Failed to remove conversation from list. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  return {
    sendMessage,
    markMessagesAsRead,
    deleteConversation,
    clearConversation,
    loading,
    error,
  };
};
